import { useState, useEffect } from 'react'
import {
  alpha,
  Box,
  Typography,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Stack,
  Button
} from '@mui/material'
import Grid from '@mui/material/Grid'
import RowStack from 'components/atoms/container/RowStack'
import { useTranslation } from 'react-i18next'
import colors from 'styles/colors'
import InCardStack from 'components/atoms/container/InCardStack'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import RolePlayDialog from 'components/molecules/rolePlay/RolePlayDialog'
import CharacterAvatar from 'components/molecules/rolePlay/CharacterAvatar'
import PublicRolePlayGetResults from 'components/molecules/rolePlay/PublicRolePlayGetResults'
import { botName } from 'utils/formatText'
import MuiMarkdown from 'mui-markdown'
import { faker } from '@faker-js/faker'
import PublicRolePlayBookDemo from 'components/molecules/rolePlay/PublicRolePlayBookDemo'
import { getPublicUser } from 'utils/localStorage'
import RolePlayScore from 'components/molecules/rolePlay/RolePlayScore'
import { RolePlayFeedback } from './RolePlayFeedback'

const PublicRolePlayResults = ({ selectedRolePlay, selectedUserRolePlay }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const publicUser = getPublicUser()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [hasImprove, setHasImprove] = useState(false)
  const [hasWell, setHasWell] = useState(false)
  const [resultPreview, setResultPreview] = useState(
    Boolean(!publicUser?.email)
  )
  const [userName, setUserName] = useState('')
  const [rolePlayReview, setRolePlayReview] = useState(
    selectedUserRolePlay?.response?.userRolePlay
  )

  const blurFilter = { filter: resultPreview ? 'blur(5px)' : 'none' }

  const AvatarBox = () => (
    <Box sx={{ ...theme.publicRolePlay.result.avatarBox, ...blurFilter }}>
      <CharacterAvatar
        rolePlay={selectedRolePlay}
        name={botName(selectedRolePlay)}
        sx={{
          ...theme.publicRolePlay.result.avatarSize
        }}
      />

      <Box sx={theme.publicRolePlay.result.avatarArc} />
      <Box sx={theme.publicRolePlay.result.avatarText}>
        <Typography variant='body1'>
          {t('publicRolePlay.thanksForChat')}

          {userName && <strong>{userName}</strong>}
        </Typography>
      </Box>
    </Box>
  )

  const ReviewBox = ({ children, bg, size = 6 }) => (
    <Grid size={size} width={'100%'}>
      <Box
        padding={12}
        sx={{
          borderRadius: 5,
          backgroundColor: alpha(bg, 0.05),
          height: '100%'
        }}>
        {children}
      </Box>
    </Grid>
  )

  const ReviewList = ({ items, type }) => (
    <List sx={{ listStyle: 'decimal', pl: 8 }}>
      {!resultPreview ? (
        <>
          {items.map((summary, index) => (
            <ListItem
              key={`review-${type}-${index}`}
              sx={{ display: 'list-item' }}>
              <ListItemText primary={summary} />
            </ListItem>
          ))}
        </>
      ) : (
        <>
          {faker.lorem
            .sentences({ min: 3, max: 6 }, '\n')
            .split('\n')
            .map((sentence, index) => (
              <ListItem
                key={`review-${type}-${index}`}
                sx={{ display: 'list-item', ...blurFilter }}>
                <ListItemText primary={sentence} />
              </ListItem>
            ))}
        </>
      )}
    </List>
  )

  const PopupButton = ({ icon, label }) => (
    <Button
      style={{
        padding: '8px 16px 8px 12px',
        borderRadius: 200,
        backgroundColor: theme.palette.background.yellow,
        width: 'fit-content',
        color: theme.palette.primary.main,
        ...blurFilter
      }}
      onClick={() => setDialogOpen(true)}
      disabled={resultPreview}>
      <RowStack spacing={2}>
        {icon}
        <Typography variant='h6' fontWeight={500}>
          {t(label)}
        </Typography>
      </RowStack>
    </Button>
  )

  const sucessCallbackFn = result => {
    console.log(result)
    setRolePlayReview(result?.userRolePlay)
    setResultPreview(false)
    setUserName(result?.userName)
  }

  useEffect(() => {
    setHasWell(rolePlayReview?.parsedReview?.WHAT_YOU_DID_WELL?.length > 0)
    setHasImprove(
      rolePlayReview?.parsedReview?.WHAT_YOU_COULD_IMPROVE_ON?.length > 0
    )
  }, [selectedRolePlay, rolePlayReview])

  return (
    <>
      <Stack
        sx={{
          ...theme.publicRolePlay.stack,
          ...theme.publicRolePlay.result.headStack
        }}>
        <Stack gap={16} flex={1}>
          <Stack
            gap={4}
            sx={{ ...theme.publicRolePlay.stack, flexDirection: 'column' }}>
            <Typography sx={theme.publicRolePlay.result.heading}>
              {selectedRolePlay.title} {t('rolePlay.results')}
            </Typography>

            <PopupButton
              icon={<ArticleOutlinedIcon />}
              label='rolePlay.scenario'
            />
          </Stack>

          <InCardStack spacing={4} alignItems={'flex-start'}>
            <RowStack justifyContent={'space-between'}>
              <Typography variant='h2'>
                {t('rolePlay.overallSummary')}
              </Typography>

              {rolePlayReview?.parsedReview?.ROLEPLAY_SCORE && (
                <RolePlayScore
                  score={rolePlayReview?.parsedReview?.ROLEPLAY_SCORE}
                  containerSx={{ ...blurFilter }}
                />
              )}
            </RowStack>

            <Box>
              {rolePlayReview?.parsedReview?.OVERALL_SUMMARY?.length > 0 && (
                <>
                  {rolePlayReview?.parsedReview.OVERALL_SUMMARY.map(
                    (summary, index) => (
                      <Typography key={`summary-${index}`} variant='body1'>
                        {summary}
                      </Typography>
                    )
                  )}
                </>
              )}

              {resultPreview && (
                <Typography variant='body1' sx={{ ...blurFilter }}>
                  {faker.lorem.sentences(5)}
                </Typography>
              )}
            </Box>
          </InCardStack>
        </Stack>

        <AvatarBox />
      </Stack>

      <Box width='100%' left={-12} top={-12} paddingBottom={12}>
        <Grid container spacing={12} justify='center'>
          {!rolePlayReview?.parsedReview?.OVERALL_SUMMARY?.length ? (
            <ReviewBox bg={theme.palette.grey[800]} size={12}>
              <MuiMarkdown>{rolePlayReview?.review}</MuiMarkdown>
            </ReviewBox>
          ) : (
            <>
              {(hasWell || resultPreview) && (
                <ReviewBox
                  size={hasImprove || resultPreview ? 6 : 12}
                  bg={colors.BrightGreen}>
                  <Typography
                    variant='h4'
                    style={{ color: colors.BrightGreen }}>
                    {t('rolePlay.whatYouDidWell')}
                  </Typography>
                  <ReviewList
                    items={rolePlayReview?.parsedReview.WHAT_YOU_DID_WELL}
                  />
                </ReviewBox>
              )}

              {(hasImprove || resultPreview) && (
                <ReviewBox
                  size={hasWell || resultPreview ? 6 : 12}
                  bg={colors.Watermelon400}>
                  <Typography variant='h4' style={{ color: colors.Watermelon400 }}>
                    {t('rolePlay.whatYouCouldImproveOn')}
                  </Typography>
                  <ReviewList
                    items={
                      rolePlayReview?.parsedReview.WHAT_YOU_COULD_IMPROVE_ON
                    }
                  />
                </ReviewBox>
              )}
            </>
          )}
        </Grid>
      </Box>
      <RolePlayFeedback
        rolePlayScore={rolePlayReview?.parsedReview?.ROLEPLAY_SCORE}
        rolePlayTitle={selectedRolePlay.title}
        source='Result page'
      />
      <RolePlayDialog
        open={dialogOpen}
        titleText={t('rolePlay.scenario')}
        confirmButtonText={t('common.done')}
        handleCancel={() => setDialogOpen(false)}
        sx={theme.publicRolePlay?.result?.scenarioDialog}>
        <Box
          dangerouslySetInnerHTML={{
            __html: selectedRolePlay.context?.html
          }}
        />
      </RolePlayDialog>

      <PublicRolePlayGetResults
        selectedUserRolePlay={selectedUserRolePlay}
        resultPreview={resultPreview}
        sucessCallbackFn={sucessCallbackFn}
      />

      {!resultPreview && <PublicRolePlayBookDemo />}
    </>
  )
}

export default PublicRolePlayResults
